/**
 * Builds Amplitude payloads for primary Dx validation and override events.
 * @module diagnosisValidatedAnalytics
 */
import { DiagnosisChangeContext } from "../../../../../api/postCall/updatePrimaryDiagnosisCode";
import {
  GeneralOrderDetails,
  PrimaryDiagnosisDetails,
} from "../../../../../data-model/medicalPaOrdersModel";
import {
  ModalNamesForBigQuery,
  ScreenNamesForBigQuery,
} from "../../../../../enums/eventsData";
import { extractRegimenCode } from "../../../../../pages/medicalNecessityAnalysis/utils/nccnCaseGate";
import { TableData } from "../../types";
import { ClinicalDataAnalyticsContext } from "../buildClinicalDataAnalytics";
import { DiagnosisOutcome, hasDxChanged } from "./diagnosisOutcome";
import {
  extractJCodesAndDrugNames,
  resolveDxSourceForCode,
} from "./diagnosisValidatedAnalyticsHelpers";

/** Inputs shared when building Dx validation / change analytics payloads. */
export interface DiagnosisValidatedAnalyticsInput {
  diagnosisOutcome: DiagnosisOutcome;
  generalOrderDetails?: GeneralOrderDetails;
  analyticsContext?: ClinicalDataAnalyticsContext;
  primaryDiagnosisDetails?: PrimaryDiagnosisDetails[];
  tableData?: TableData[];
  changeContext?: DiagnosisChangeContext;
  screenName?: string;
}

/**
 * Builds `additional_data` for `primary_diagnosis_validated`.
 *
 * @param input - Outcome plus order/analytics context available at save time.
 * @returns Self-describing event attributes (excludes backend-only fields).
 */
export const buildPrimaryDiagnosisValidatedAdditionalData = (
  input: DiagnosisValidatedAnalyticsInput,
): Record<string, unknown> => {
  const {
    diagnosisOutcome,
    generalOrderDetails,
    analyticsContext,
    primaryDiagnosisDetails,
    tableData,
    screenName,
  } = input;

  const regimenName = generalOrderDetails?.payload?.regimen_name ?? "";
  const { jCodes, drugNames } = extractJCodesAndDrugNames(
    analyticsContext?.authOnFile,
  );
  const stackDx = diagnosisOutcome.stack_dx_code;
  const finalDx = diagnosisOutcome.user_dx_code;

  return {
    screen_name: screenName ?? ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
    modal_name: ModalNamesForBigQuery.CLINICAL_DATA_TABLE,
    outcome: diagnosisOutcome.outcome,
    stack_dx_code: stackDx,
    final_dx_code: finalDx,
    was_on_chart: diagnosisOutcome.was_on_chart,
    regimen_name: regimenName,
    regimen_code: extractRegimenCode(regimenName) ?? "",
    j_codes: jCodes,
    drug_names: drugNames,
    stack_dx_source: stackDx
      ? resolveDxSourceForCode(stackDx, primaryDiagnosisDetails, tableData)
      : "none",
    final_dx_source: finalDx
      ? resolveDxSourceForCode(finalDx, primaryDiagnosisDetails, tableData)
      : "none",
    was_stack_shown: stackDx !== "",
    dx_changed: hasDxChanged(diagnosisOutcome),
    auth_status:
      generalOrderDetails?.status?.master_auth_status ??
      generalOrderDetails?.status?.medical_order_status ??
      "",
    payer_name: analyticsContext?.payerName ?? "",
    assigned_to: generalOrderDetails?.assigned_to?.assigned_to_name ?? "",
  };
};

/**
 * Builds `additional_data` for `dx_code_change_submitted` (free-text reason/source).
 *
 * @param input - Outcome plus optional change context from the Dx modal.
 * @returns Override event attributes for Amplitude.
 */
export const buildDxCodeChangeSubmittedAdditionalData = (
  input: DiagnosisValidatedAnalyticsInput,
): Record<string, unknown> => {
  const { diagnosisOutcome, analyticsContext, changeContext, screenName } =
    input;
  const { jCodes } = extractJCodesAndDrugNames(analyticsContext?.authOnFile);

  return {
    screen_name: screenName ?? ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
    modal_name: ModalNamesForBigQuery.CLINICAL_DATA_TABLE,
    j_code: jCodes[0] ?? "",
    j_codes: jCodes,
    stack_dx_code: diagnosisOutcome.stack_dx_code,
    new_dx_code: diagnosisOutcome.user_dx_code,
    change_reason_text: changeContext?.userReason ?? "",
    change_source_detail: changeContext?.userSource ?? "",
    is_on_chart: diagnosisOutcome.was_on_chart,
    payer_name: analyticsContext?.payerName ?? "",
  };
};
